import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  limit,
  startAfter,
  getDocs,
} from "firebase/firestore";
import { databasePromise } from "configuration";
import { reviewService } from "services";

const PAGE_SIZE = 20;

const filterReviewsByStatus = (reviews, userId) => {
  return reviews.filter((review) => {
    if (review.status !== "pendingCreate") return true;
    return review.userId === userId;
  });
};

const filterReviewsByParentStatus = (reviews) => {
  const hiddenStatuses = ["pendingDelete", "deleted"];
  return reviews.filter((review) => {
    if (review.dealId) {
      if (review.dealStatus && hiddenStatuses.includes(review.dealStatus)) return false;
      if (review.restaurantStatus && hiddenStatuses.includes(review.restaurantStatus)) return false;
    }
    if (!review.dealId && review.restaurantId) {
      if (review.restaurantStatus && hiddenStatuses.includes(review.restaurantStatus)) return false;
    }
    return true;
  });
};

const buildQuery = (reviewsRef, type, id, userId, contentFilter, lastDoc = null) => {
  let constraints = [];

  if (type === "user") {
    constraints = [
      where("userId", "==", userId),
      where("status", "in", ["approved", "pendingCreate", "pendingEdit", "pendingDelete", "pendingReport", "reported"]),
      orderBy("createdDate", "desc"),
    ];
    if (contentFilter === "deals") constraints.splice(1, 0, where("dealId", "!=", null));
    else if (contentFilter === "restaurants") constraints.splice(1, 0, where("dealId", "==", null));
  } else if (type === "restaurant") {
    constraints = [
      where("restaurantId", "==", id),
      where("dealId", "==", null),
      where("status", "in", ["approved", "pendingCreate", "pendingEdit", "pendingDelete", "pendingReport"]),
      orderBy("createdDate", "desc"),
    ];
  } else {
    constraints = [
      where("dealId", "==", id),
      where("status", "in", ["approved", "pendingCreate", "pendingEdit", "pendingDelete", "pendingReport"]),
      orderBy("createdDate", "desc"),
    ];
  }

  constraints.push(limit(PAGE_SIZE));
  if (lastDoc) constraints.push(startAfter(lastDoc));

  return query(reviewsRef, ...constraints);
};

export const useReviews = ({
  id,
  type = "deal",
  userId = null,
  contentFilter = null,
  sortBy = "newest",
}) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const lastDocRef = useRef(null);
  const likeTimers = useRef({});
  const dislikeTimers = useRef({});

  useEffect(() => {
    const likeTimersCurrent = likeTimers.current;
    const dislikeTimersCurrent = dislikeTimers.current;
    return () => {
      Object.values(likeTimersCurrent).forEach(clearTimeout);
      Object.values(dislikeTimersCurrent).forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    if (type === "user" && !userId) {
      setReviews([]);
      setLoading(false);
      return;
    }
    if (type !== "user" && !id) {
      setReviews([]);
      setLoading(false);
      return;
    }

    // Reset pagination on param change
    lastDocRef.current = null;
    setReviews([]);
    setHasMore(false);
    setLoading(true);

    let unsubscribe;
    let cancelled;

    databasePromise.then((db) => {
      if (cancelled) return;
      const reviewsRef = collection(db, "reviews");
      const q = buildQuery(reviewsRef, type, id, userId, contentFilter);

      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const reviewsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] ?? null;
          setHasMore(snapshot.docs.length === PAGE_SIZE);

          const filtered = filterReviewsByParentStatus(reviewsData);
          const filteredReviews = filterReviewsByStatus(filtered, userId);

          setReviews(filteredReviews);
          setLoading(false);
        },
        (err) => {
          console.error("Error fetching reviews:", err);
          setError(err.message);
          setLoading(false);
        }
      );
    });

  return () => {
    cancelled = true;
    unsubscribe?.();
  };
  }, [id, type, userId, contentFilter]);

  const loadMore = useCallback(async () => {
    if (!lastDocRef.current || loadingMore || !hasMore) return;

    setLoadingMore(true);
    try {
      const db = await databasePromise;
      const reviewsRef = collection(db, "reviews");
      const q = buildQuery(reviewsRef, type, id, userId, contentFilter, lastDocRef.current);
      const snapshot = await getDocs(q);

      const newReviews = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] ?? null;
      setHasMore(snapshot.docs.length === PAGE_SIZE);

      const filtered = filterReviewsByParentStatus(newReviews);
      const filteredNew = filterReviewsByStatus(filtered, userId);

      setReviews((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const deduped = filteredNew.filter((r) => !existingIds.has(r.id));
        return [...prev, ...deduped];
      });
    } catch (err) {
      console.error("Error loading more reviews:", err);
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [id, type, userId, contentFilter, loadingMore, hasMore]);

  const sortedReviews = useMemo(() => {
    const sorted = [...reviews];
    switch (sortBy) {
      case "newest":
        sorted.sort((a, b) => {
          const aDate = a.createdDate?.toDate ? a.createdDate.toDate() : new Date(a.createdDate || 0);
          const bDate = b.createdDate?.toDate ? b.createdDate.toDate() : new Date(b.createdDate || 0);
          return bDate - aDate;
        });
        break;
      case "oldest":
        sorted.sort((a, b) => {
          const aDate = a.createdDate?.toDate ? a.createdDate.toDate() : new Date(a.createdDate || 0);
          const bDate = b.createdDate?.toDate ? b.createdDate.toDate() : new Date(b.createdDate || 0);
          return aDate - bDate;
        });
        break;
      case "ratingHigh":
        sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case "ratingLow":
        sorted.sort((a, b) => (a.rating || 0) - (b.rating || 0));
        break;
      case "mostLiked":
        sorted.sort((a, b) => (b.likedBy?.length || 0) - (a.likedBy?.length || 0));
        break;
      default:
        break;
    }
    return sorted;
  }, [reviews, sortBy]);

  // --- Write operations — all delegated to reviewService ---

  const createReview = useCallback(async (reviewData) => {
    try {
      setError(null);
      const optimisticReview = {
        id: `temp-${Date.now()}`,
        ...reviewData,
        status: "pendingCreate",
        createdDate: new Date().toISOString(),
        isOptimistic: true,
      };
      setReviews((prev) => [optimisticReview, ...prev]);
      const result = await reviewService.createReview(reviewData);
      setReviews((prev) => prev.filter((r) => r.id !== optimisticReview.id));
      return result;
    } catch (err) {
      setError(err.message);
      setReviews((prev) => prev.filter((r) => !r.isOptimistic));
      return { success: false, error: err.message };
    }
  }, []);

  const updateReview = useCallback(async (reviewId, editData, currentStatus) => {
    try {
      setError(null);
      return await reviewService.updateReview(reviewId, editData, currentStatus);
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const deleteReview = useCallback(async (review) => {
    try {
      setError(null);
      return await reviewService.deleteReview(review);
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const deleteReviewDraft = useCallback(async (reviewId) => {
    try {
      setError(null);
      return await reviewService.deleteReviewDraft(reviewId);
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const likeReview = useCallback((reviewId, userId) => {
    clearTimeout(likeTimers.current[reviewId]);
    likeTimers.current[reviewId] = setTimeout(async () => {
      try {
        const review = reviews.find((r) => r.id === reviewId);
        const isLiked = review?.likedBy?.includes(userId);
        const isDisliked = review?.dislikedBy?.includes(userId);
        if (isLiked) {
          await reviewService.removeLike(reviewId, userId);
        } else {
          await reviewService.addLike(reviewId, userId, isDisliked);
        }
      } catch (err) {
        // Don't surface like errors as fatal — just log them
        console.error("Error liking review:", err);
      }
    }, 300);
  }, [reviews]);

  const dislikeReview = useCallback((reviewId, userId) => {
    clearTimeout(dislikeTimers.current[reviewId]);
    dislikeTimers.current[reviewId] = setTimeout(async () => {
      try {
        const review = reviews.find((r) => r.id === reviewId);
        const isDisliked = review?.dislikedBy?.includes(userId);
        const isLiked = review?.likedBy?.includes(userId);
        if (isDisliked) {
          await reviewService.removeDislike(reviewId, userId);
        } else {
          await reviewService.addDislike(reviewId, userId, isLiked);
        }
      } catch (err) {
        // Don't surface dislike errors as fatal — just log them
        console.error("Error disliking review:", err);
      }
    }, 300);
  }, [reviews]);

  const reportReview = useCallback(async (reviewId, userId, reportData) => {
    try {
      setError(null);
      return await reviewService.reportReview(reviewId, userId, reportData);
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const cancelDeleteReview = useCallback(async (review) => {
    try {
      setError(null);
      return await reviewService.cancelDeleteReview(review);
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  return {
    reviews: sortedReviews,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    createReview,
    updateReview,
    deleteReview,
    deleteReviewDraft,
    likeReview,
    dislikeReview,
    reportReview,
    cancelDeleteReview,
  };
};
